/** Накопления: норма сбережений, буфер и распределение 50/30/20.
 *
 * Считает `MethodologyService` — он был написан целиком и никогда не подключался,
 * хотя новых данных ему не нужно вовсе.
 *
 * Тон: 50/30/20 здесь ОРИЕНТИР, а не лимит. Превысить его — не нарушение, а
 * повод посмотреть, куда уходит. Поэтому ни «слишком», ни «превысил», ни красного
 * на отклонении: засечка показывает, где ориентир, и на этом всё.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Failure, type Savings as Data } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { percent, widthOf } from "../lib/percent.ts";
import { plural } from "../lib/plural.ts";
import { Fallback } from "./Fallback.tsx";
import { Header } from "../components/Header.tsx";
import { card, hint } from "../components/ui.tsx";

const LABELS: Record<string, string> = {
  need: "Нужное", want: "Хотелки", savings: "Сбережения",
};
const COLORS: Record<string, string> = {
  need: "var(--cat-2)", want: "var(--cat-5)", savings: "var(--income)",
};

/** Считать не из чего — это не «ноль месяцев», а «все месяцы по нулям».
 *
 *  Сервис всегда возвращает окно целиком, дозаполняя пустые месяцы нулями:
 *  проверка на длину списка не срабатывала НИКОГДА, и новый человек видел три
 *  карточки с нулями вместо объяснения.
 */
function пусто(data: Data): boolean {
  return data.months.every((m) => m.income === "0" && m.expense === "0");
}

export function Savings({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Data | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    setFailure(null);
    api.savings().then(setData)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Накопления" onBack={onBack} />

      {loading || !data ? <Skeleton /> : пусто(data) ? (
        /* Пустого состояния тут не было вовсе: без записей экран показывал три
           карточки с нулями и пустой прямоугольник вместо разбивки. Нули — не
           данные, а их отсутствие, и говорить об этом надо словами. */
        <section style={{ ...card, textAlign: "center" }}>
          <p style={{ fontSize: "var(--f-body)", fontWeight: 600, margin: 0 }}>
            Считать пока не из чего
          </p>
          <p style={{ ...hint, marginBottom: 0 }}>
            Норма сбережений, буфер и разбивка 50/30/20 считаются по закрытым месяцам.
            Запиши траты и доходы за месяц — и здесь появятся цифры.
          </p>
        </section>
      ) : (
        <>
          <section style={card}>
            <div style={label}>Норма сбережений</div>
            <div className="num" style={hero}>{percent(data.rate_window)}</div>
            <div style={{ ...hint, marginTop: 4 }}>
              в среднем за {plural(data.window_months, ["месяц", "месяца", "месяцев"])}
            </div>
            <Months data={data} />
          </section>

          <section style={{ ...card, marginTop: "var(--s-3)" }}>
            <div style={label}>Буфер</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
              {/* Дробное число всегда требует родительного: «1,4 месяца», а не
                  «1,4 месяц». Округление для выбора формы не годится — оно
                  давало «1 месяц» при 1,4. Разделитель запятая, как в деньгах. */}
              <span className="num" style={{ ...hero, fontSize: "var(--f-t2)" }}>
                {Number(data.buffer_months).toFixed(1).replace(".", ",")}
              </span>
              <span style={{ fontSize: "var(--f-body)", color: "var(--text-muted)" }}>месяца</span>
            </div>
            <p style={hint}>
              При тратах {formatMoney(data.baseline_monthly, data.base_currency)} в месяц
              накопленного {formatMoney(data.buffer, data.base_currency)} хватает на столько.
              {data.baseline_is_default
                ? " Норма взята по среднему за окно: свой ориентир можно задать лимитом на главной."
                : " Норма — твой месячный лимит."}
            </p>
          </section>

          <section style={{ ...card, marginTop: "var(--s-3)" }}>
            <div style={label}>Этот месяц</div>
            <div className="num" style={{ ...hero, fontSize: "var(--f-t2)" }}>
              {formatMoney(data.spent_mtd, data.base_currency)}
            </div>
            <p style={hint}>
              За {plural(data.days_elapsed, ["день", "дня", "дней"])} из {data.days_in_month}.
              При таком темпе к концу месяца выйдет{" "}
              {formatMoney(data.forecast_month, data.base_currency)}.
            </p>
          </section>

          <h2 style={heading}>Куда уходит</h2>
          <section style={{ ...card, padding: "4px var(--s-5)" }}>
            {data.buckets.map((b, i) => (
              <BucketRow key={b.bucket} bucket={b} base={data.base_currency}
                         last={i === data.buckets.length - 1} />
            ))}
          </section>
          <p style={{ ...hint, marginTop: "var(--s-3)" }}>
            50/30/20 — ориентир, а не лимит: засечка показывает, где он.
            {Number(data.coverage) < 0.9 && (
              <> Размечено {percent(data.coverage)} трат, остальные{" "}
              {formatMoney(data.unclassified, data.base_currency)} в доли не вошли —
              тип категории задаётся в её настройках.</>
            )}
          </p>
        </>
      )}
    </div>
  );
}

/** Месяцы окна списком, а не столбиками.
 *
 * Окно по умолчанию — три месяца, и график из трёх колонок не сообщает ничего,
 * чего не сообщает строка: он выглядит как три плашки и заставляет их сравнивать
 * глазом там, где проще прочитать число.
 */
function Months({ data }: { data: Data }) {
  if (data.months.length === 0) return null;
  return (
    <div style={{ marginTop: "var(--s-4)" }}>
      {data.months.map((m) => {
        const negative = m.rate.startsWith("-");
        return (
          <div key={`${m.year}-${m.month}`}
               style={{ display: "flex", alignItems: "baseline", gap: "var(--s-3)",
                        padding: "7px 0" }}>
            <span style={{ flexGrow: 1, fontSize: "var(--f-caption)",
                           color: "var(--text-muted)" }}>
              {MONTH_NAMES[m.month - 1]}
            </span>
            <span className="num" style={{ fontSize: "var(--f-caption)",
                                           color: "var(--text-muted)" }}>
              {formatMoney(m.expense, data.base_currency)}
            </span>
            <span className="num" style={{ fontSize: "var(--f-body-s)", fontWeight: 600,
                                           minWidth: 46, textAlign: "right",
                                           color: negative ? "var(--text-muted)" : "var(--text)" }}>
              {percent(m.rate)}
            </span>
          </div>
        );
      })}
      <p style={{ ...hint, marginTop: "var(--s-2)" }}>
        Отрицательный месяц — потратил больше, чем заработал. Бывает.
      </p>
    </div>
  );
}

const MONTH_NAMES = ["январь", "февраль", "март", "апрель", "май", "июнь",
                     "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"];

function BucketRow({ bucket, base, last }: {
  bucket: Data["buckets"][number]; base: string; last: boolean;
}) {
  return (
    <div style={{ padding: "13px 0",
                  borderBottom: last ? "none" : "1px solid var(--separator)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: "var(--f-body)" }}>{LABELS[bucket.bucket] ?? bucket.bucket}</span>
        <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
          {percent(bucket.share)}
        </span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--surface-3)",
                    marginTop: 9, position: "relative", overflow: "hidden" }}>
        <div style={{ width: `${widthOf(bucket.share)}%`, height: "100%",
                      background: COLORS[bucket.bucket] ?? "var(--data)" }} />
        <div aria-hidden="true"
             style={{ position: "absolute", left: `${widthOf(bucket.target)}%`, top: -2,
                      width: 2, height: 10, background: "var(--text)", opacity: 0.9 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        <span className="num" style={{ fontSize: "var(--f-caption-s)", color: "var(--text-muted)" }}>
          {formatMoney(bucket.amount, base)}
        </span>
        <span style={{ fontSize: "var(--f-caption-s)", color: "var(--text-faint)" }}>
          ориентир {percent(bucket.target)}
        </span>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю накопления">
      <div style={{ height: 30, width: "35%", background: "var(--surface-3)", borderRadius: 7 }} />
      <div style={{ height: 48, background: "var(--surface-2)", borderRadius: 8,
                    marginTop: "var(--s-4)" }} />
    </div>
  );
}

const label: React.CSSProperties = {
  fontSize: "var(--f-caption)", color: "var(--text-muted)",
};
const hero: React.CSSProperties = {
  fontSize: "var(--f-t1)", fontWeight: 700, letterSpacing: "-0.8px", marginTop: 4,
};
const heading: React.CSSProperties = {
  fontSize: "var(--f-t3)", fontWeight: 600, margin: "var(--s-6) 0 var(--s-3)",
};
